/*!
 *
 * Angle - Bootstrap Admin Template
 *
 * Version: 4.7.1
 * Author: @themicon_co
 * Website: http://themicon.co
 * License: https://wrapbootstrap.com/help/licenses
 *
 */

import { enableProdMode } from "@angular/core";
import { environment } from "./environments/environment";
import { provideRouter, withHashLocation } from "@angular/router";
import { provideHttpClient, HttpClient, withInterceptors } from "@angular/common/http";
import { bootstrapApplication } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideTranslateService, TranslateLoader } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import {
  StsConfigHttpLoader,
  StsConfigLoader,
  provideAuth,
} from "angular-auth-oidc-client";
import { MsalService, MSAL_INSTANCE } from "@azure/msal-angular";
import { PublicClientApplication } from "@azure/msal-browser";
import { SettingsService } from "./app/core/settings/settings.service";
import { AuthConfigService } from "./app/core/appsettings/auth-config.service";
import { AppComponent } from "./app/app.component";
import { routes } from "./app/routes/routes";
import { AppSettings } from "./app/core/appsettings/appsettings";
import { TranslatorService } from "./app/core/translator/translator.service";
import { httpInterceptor } from "./app/core/api_services/httpinterceptor.service";
import { MenuService } from "./app/core/menu/menu.service";
import { UserblockService } from "./app/layout/sidebar/userblock/userblock.service";
import { ThemesService } from "./app/core/themes/themes.service";
import { ModalService } from "./app/core/modalservice/modal.service";
import { DeviceService } from "./app/core/api_services/device.services";
import { of } from "rxjs";
import { LicenseService } from "./app/core/api_services/license.service";
import { AccountService } from "./app/core/api_services/account.service";
import { InvoiceService } from "./app/core/api_services/invoice.service";
import { provideMomentDateAdapter } from "@angular/material-moment-adapter";
import localeDe from "@angular/common/locales/de";
import { registerLocaleData } from "@angular/common";

registerLocaleData(localeDe, "de-DE");

// Enable production mode if applicable
if (environment.production) {
  enableProdMode();
}

const authFactory = (configService: AuthConfigService) => {
  const config = configService.getConfig();
  return new StsConfigHttpLoader(of(config));
};

export function MSALInstanceFactory() {
  return new PublicClientApplication({
    auth: {
      clientId: "bd90b2c0-e6f3-4985-a242-36d4eeee3495",
      authority: "https://login.microsoftonline.com/common",
      redirectUri: AppSettings.REDIRECT_URI,
    },
  });
}

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(routes, withHashLocation()),

    provideHttpClient(withInterceptors([httpInterceptor])),
    provideAnimations(),

    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),

    provideAuth({
      loader: {
        provide: StsConfigLoader,
        useFactory: authFactory,
        deps: [AuthConfigService],
      },
    }),

    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    MsalService,
    SettingsService,
    TranslatorService,
    MenuService,
    UserblockService,
    ThemesService,
    ModalService,
    DeviceService,
    LicenseService,
    AccountService,
    InvoiceService,
    ...provideMomentDateAdapter({
      parse: { dateInput: "DD/MM/YYYY" },
      display: {
        dateInput: "DD/MM/YYYY",
        monthYearLabel: "MMM YYYY",
        dateA11yLabel: "LL",
        monthYearA11yLabel: "MMMM YYYY",
      },
    }),
  ],
})
  .then(() => {
    (window as any).appBootstrap();
  })
  .catch((err) => console.error(err));
